<script lang="ts">
	/**
	 * The payment form for a provider that hosts none of its own (Design §6.2).
	 *
	 * Braintree has no equivalent of a hosted checkout page: it issues a client token and you render
	 * its Drop-in on your own page. Card data still never reaches these servers — Drop-in is
	 * Braintree's own cross-origin iframes, and what this component emits is a single-use token
	 * naming details Braintree is holding, never a card number (D3). What changes versus a hosted
	 * page is only *whose page it is*.
	 *
	 * It owns the widget and nothing else. It does not know what is being bought, it does not create
	 * anything, and it does not decide what happens on success — the caller does all three, which is
	 * what lets the same component serve a checkout and the "add a card" flow on `/Account/Billing`.
	 *
	 * The Drop-in library is loaded **dynamically**, and that is not a size optimisation. It is a
	 * vendor SDK that reaches for the network and touches `window` at import time, so a static
	 * import would pull it into the bundle every page loads and run it in every component test in
	 * the suite — including the ones about products, which have no business booting a payment SDK.
	 */

	import { createEventDispatcher, onDestroy } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import EmptyState from './EmptyState.svelte';
	import { colorTheme } from '../AbstractID';

	/** The client token the server minted for this customer. Null while it is being fetched. */
	export let clientToken: string | null = null;
	/** What the submit button says. The caller knows what it is committing to; this does not. */
	export let submitLabel: string = 'Pay';
	/** Set while the caller is doing something with the token this emitted. */
	export let busy: boolean = false;
	/** The caller's own failure, rendered under the form rather than replacing it, so it can be retried. */
	export let error: string = '';
	/** Set while something the caller needs first is missing — the billing address, on `/Checkout/Pay`. */
	export let disabled: boolean = false;

	/** What the page falls back to before its own stylesheet has resolved a body font. */
	const SYSTEM_FONT_STACK =
		'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

	const dispatch = createEventDispatcher<{ token: { paymentToken: string } }>();

	let container: HTMLDivElement | null = null;
	/** Braintree's Drop-in instance. Untyped because the package ships its own and we hold one field. */
	let instance: { requestPaymentMethod: () => Promise<{ nonce: string }>; teardown: () => Promise<void> } | null = null;
	let phase: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
	let loadError: string = '';
	/** Braintree's own refusal of what the customer just entered — a declined card, a bad CVV. */
	let formError: string = '';

	/**
	 * Rebuilds the widget whenever the token changes, and only then.
	 *
	 * Keyed on the token rather than on `container` because the element is stable and the token is
	 * not: a reactive block that also watched the node would tear down and rebuild the form on every
	 * unrelated render, which loses whatever the customer had typed into it.
	 */
	$: if (clientToken !== null && container !== null && phase === 'idle') {
		void mount(clientToken, container);
	}

	async function mount(token: string, into: HTMLDivElement): Promise<void> {
		phase = 'loading';
		loadError = '';
		try {
			const dropin = (await import('braintree-web-drop-in')).default;
			/*
			 * Captured **with** the styles, not after the widget is built.
			 *
			 * `create` is a round trip: it loads the SDK and stands up three iframes. Recording the
			 * theme after it resolves records whatever the theme is *then*, so a change during those
			 * few hundred milliseconds would be remembered as the theme the frames were told about
			 * when they were told the other one — and the rebuild below would never fire, leaving
			 * exactly the illegible form it exists to prevent.
			 */
			const styles = iframeStyles();
			const theme = $colorTheme;

			instance = await dropin.create({
				authorization: token,
				container: into,
				// The card form alone. Every other method Braintree offers — PayPal, Venmo, the
				// wallets — needs its own account-side configuration, and a button that appears and
				// then fails at submit is worse than one that was never offered.
				card: {
					cardholderName: { required: false },
					overrides: { styles }
				}
			});
			themeAtMount = theme;
			phase = 'ready';
		} catch (caught) {
			// The widget could not be built: an expired token, a blocked script, an account with no
			// payment method enabled. The caller's page renders this rather than an empty box, which
			// is what a bare failure would leave behind.
			loadError = (caught as Error).message;
			phase = 'failed';
		}
	}

	/**
	 * Asks the widget for a token and hands it up.
	 *
	 * Braintree rejects here when the customer's own input is wrong — an incomplete number, a
	 * declined verification — and that rejection carries the sentence they need. It is rendered
	 * beside the form rather than thrown, because the form is still on screen and still correctable.
	 */
	async function submit(): Promise<void> {
		if (instance === null) {
			return;
		}
		formError = '';
		try {
			const { nonce } = await instance.requestPaymentMethod();
			dispatch('token', { paymentToken: nonce });
		} catch (caught) {
			formError = (caught as Error).message;
		}
	}

	/**
	 * The half of the styling that cannot be done in CSS.
	 *
	 * The card number, expiry and CVV are **cross-origin iframes** — that is the whole point, since
	 * it is what keeps card data off these servers (D3) — so no stylesheet of ours reaches them.
	 * Braintree accepts a small allowlist of properties to apply *inside* those frames instead, and
	 * this is the only channel there is.
	 *
	 * The values are read from the live document rather than written out twice: the CSS below
	 * defines them as custom properties on the widget, so the palette has one home and the frames
	 * are told whatever the page resolved. Hard-coding them here would be a second copy that drifts,
	 * and it would drift *only in dark mode*, where nobody looks.
	 */
	function iframeStyles(): Record<string, Record<string, string>> {
		const probe = document.createElement('div');
		probe.className = 'braintree-dropin';
		probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none';
		document.body.appendChild(probe);

		const computed = getComputedStyle(probe);
		const token = (name: string, fallback: string): string =>
			computed.getPropertyValue(name).trim() || fallback;

		const styles = {
			input: {
				color: token('--bt-text', '#18181b'),
				// Falls back rather than sending an empty declaration: a document that has not
				// resolved a body font yet would otherwise hand the frames `font-family: ''`, which
				// is not a valid value and leaves them on Braintree's own default — the one visible
				// difference between our fields and theirs.
				'font-family': getComputedStyle(document.body).fontFamily || SYSTEM_FONT_STACK,
				'font-size': '0.875rem',
				'font-weight': '400'
			},
			':focus': { color: token('--bt-text', '#18181b') },
			'::placeholder': { color: token('--bt-muted', '#a1a1aa') },
			// Braintree marks a field invalid as the customer types, so this is the colour they see
			// while correcting a number rather than only after a refusal.
			'.invalid': { color: token('--bt-danger', '#ef4444') }
		};

		probe.remove();
		return styles;
	}

	/**
	 * The theme the frames were told about, and the reason changing it rebuilds the widget.
	 *
	 * A stylesheet re-resolves on a theme change; an iframe told a colour at creation does not, and
	 * Drop-in exposes no way to restyle one afterwards. So a customer who flips to dark mode with
	 * the form open would be typing near-black text on a near-black field — legible in neither
	 * theme's terms and invisible to anyone testing in light mode only.
	 *
	 * Rebuilding costs whatever they had typed. That is a real cost, and it is the smaller one: the
	 * alternative is a form they cannot read, reached by a deliberate action they can undo.
	 */
	let themeAtMount: 'dark' | 'light' | null = null;
	$: if (phase === 'ready' && themeAtMount !== null && $colorTheme !== themeAtMount && container !== null) {
		void remount(container);
	}

	async function remount(into: HTMLDivElement): Promise<void> {
		const previous = instance;
		instance = null;
		phase = 'idle';
		await previous?.teardown().catch(() => undefined);
		if (clientToken !== null) {
			await mount(clientToken, into);
		}
	}

	onDestroy(() => {
		// The widget holds iframes and listeners on `window`. Leaving them behind survives the
		// route change and the next Drop-in then mounts beside a dead one.
		void instance?.teardown().catch(() => undefined);
		instance = null;
	});
</script>

<div class="space-y-3">
	{#if phase === 'failed'}
		<EmptyState
			variant="error"
			title="The payment form could not be loaded"
			description={loadError}
		/>
	{:else if phase !== 'ready'}
		<EmptyState variant="loading" title="Loading the payment form…" />
	{/if}

	<!--
		Always in the markup, never behind the `{#if}` above: Braintree mounts into this node, so a
		conditional would mean the element does not exist at the moment `create` is asked to fill it.
	-->
	<div bind:this={container} class:hidden={phase !== 'ready'}></div>

	{#if formError !== ''}
		<p class="text-sm text-red-600 dark:text-red-400" role="alert">{formError}</p>
	{/if}
	{#if error !== ''}
		<p class="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
	{/if}

	<Button
		type="button"
		class="w-full"
		disabled={phase !== 'ready' || busy || disabled}
		on:click={submit}
	>
		{busy ? 'Working…' : submitLabel}
	</Button>
</div>

<style>
	/*
	 * Drop-in's own chrome, made to read as the rest of the store's forms.
	 *
	 * `:global` because Braintree builds this DOM imperatively inside our container, so Svelte's
	 * scoping class is never applied to any of it — a scoped rule here would compile, ship, and
	 * match nothing.
	 *
	 * The palette lives here and only here. `iframeStyles()` reads these custom properties back off
	 * the element and hands them to the card frames, which no stylesheet can reach; writing the
	 * colours out a second time in the script is how the two come to disagree, and they would
	 * disagree in dark mode, which is where nobody checks.
	 *
	 * The values match flowbite's `Input` rather than inventing a surface, because "looks like the
	 * store's UI" means the control beside it — the country picker, the coupon field — not a new
	 * thing that is merely tasteful. Zinc, because the Tailwind theme maps `gray` to it so the
	 * light and dark depth ramps share a hue.
	 */
	:global(.braintree-dropin) {
		--bt-surface: theme('colors.gray.50');
		--bt-border: theme('colors.gray.300');
		--bt-border-focus: theme('colors.primary.500');
		--bt-text: theme('colors.gray.900');
		--bt-muted: theme('colors.gray.500');
		--bt-danger: theme('colors.red.500');
	}

	:global(.dark .braintree-dropin) {
		--bt-surface: theme('colors.gray.700');
		--bt-border: theme('colors.gray.600');
		--bt-text: #ffffff;
		--bt-muted: theme('colors.gray.400');
		--bt-danger: theme('colors.red.400');
	}

	/* Braintree ships its own card and border; ours is the `widget` this sits inside. */
	:global(.braintree-dropin .braintree-sheet) {
		background: transparent;
		border: 0;
		margin: 0;
	}

	/*
	 * The sheet header is a card-brand strip and the word "Card". With one payment method enabled it
	 * names the only thing on screen, so it is the definition of chrome — and removing it puts the
	 * fields directly under whatever the calling page titled the step.
	 */
	:global(.braintree-dropin .braintree-sheet__header) {
		display: none;
	}

	:global(.braintree-dropin .braintree-sheet__content--form) {
		padding: 0;
	}

	:global(.braintree-dropin .braintree-form__label) {
		color: var(--bt-muted);
		font-size: 0.75rem;
		font-weight: 500;
		margin-bottom: 0.25rem;
	}

	/* The field wrapper is ours; only the text inside it belongs to the iframe. */
	:global(.braintree-dropin .braintree-form__hosted-field),
	:global(.braintree-dropin .braintree-form__field input) {
		background-color: var(--bt-surface);
		border: 1px solid var(--bt-border);
		border-radius: 0.5rem;
		color: var(--bt-text);
		height: 2.625rem;
		padding: 0 0.75rem;
	}

	:global(.braintree-dropin .braintree-form__field-group--card-type-known .braintree-form__hosted-field) {
		background-position: right 0.75rem center;
	}

	/*
	 * Focus is drawn on the wrapper rather than inside the frame: `:focus` in the frame reaches only
	 * the text, and a field whose border does not move on focus reads as disabled.
	 */
	:global(.braintree-dropin .braintree-form__field-group--is-focused .braintree-form__hosted-field) {
		border-color: var(--bt-border-focus);
		box-shadow: 0 0 0 1px var(--bt-border-focus);
		outline: none;
	}

	:global(.braintree-dropin .braintree-form__field-group--has-error .braintree-form__hosted-field) {
		border-color: var(--bt-danger);
	}

	:global(.braintree-dropin .braintree-form__field-error) {
		color: var(--bt-danger);
		font-size: 0.75rem;
		margin-top: 0.25rem;
	}

	:global(.braintree-dropin .braintree-form__field-error-icon) {
		display: none;
	}

	/* Braintree's own inline spacing, replaced with the app's field rhythm. */
	:global(.braintree-dropin .braintree-form__field-group) {
		margin-bottom: 0.75rem;
	}

	:global(.braintree-dropin .braintree-form__field-group:last-child) {
		margin-bottom: 0;
	}

	/* A saved card, when the client token names a customer who has one. */
	:global(.braintree-dropin .braintree-method) {
		background-color: var(--bt-surface);
		border-color: var(--bt-border);
		border-radius: 0.5rem;
	}

	:global(.braintree-dropin .braintree-method--active) {
		border-color: var(--bt-border-focus);
	}

	:global(.braintree-dropin .braintree-large-button) {
		color: var(--bt-muted);
	}
</style>
